class MatQuiz extends HTMLElement {
  constructor() {
    // Always call super first in constructor
    super();

    this.elements = {
      form: this.querySelector("form"),
      navBtn: this.querySelectorAll("[data-js-nav-button]"),
      answerInput: this.querySelectorAll("[data-answer-input]"),
      progressBarItem: this.querySelectorAll("[data-progress-bar-item"),
      viewResultsBtn: this.querySelector("[data-js-view-results]"),
      resetQuizBtn: this.querySelectorAll("[data-js-reset-quiz]"),
      views: this.querySelectorAll("[data-view]"),
      resultsList: this.querySelector("#results-product-list"),
    };

    const scoringDataClone = this.getDeepClone(window.matQuiz.scoringData);

    this._state = {};
    this.setState("scoringData", scoringDataClone);
    this.setState("topProducts", []);
    this.setState("currentQuestionIndex", 1);
    this.setState(
      "lastQuestionIndex",
      this.querySelectorAll("[data-js-question]").length
    );
    this.setState("view", "main");
    this.setState("productResultsData", []);
    this.setState("userAnswersData", []);
    this.setState("userCustomProperties", []); // used for Klaviyo profile custom properties
  }

  getState(path) {
    // The state variable was initialised in the constructor
    return this._state[path];
  }

  setState(path, value) {
    if (this._state[path] !== value) {
      this._state[path] = value;
    }
  }

  connectedCallback() {
    this.elements.form.addEventListener("submit", this.onFormSubmit.bind(this));
    this.elements.navBtn.forEach((el) => {
      el.addEventListener("click", this.onNavBtnClick.bind(this));
    });
    this.elements.answerInput.forEach((el) => {
      el.addEventListener("click", this.onAnswerChange.bind(this));
    });
    this.elements.viewResultsBtn.addEventListener("click", () => {
      this.setView("results");
    });
    this.elements.resetQuizBtn.forEach((el) => {
      el.addEventListener("click", () => {
        this.resetQuiz();
      });
    });
  }

  /**
   * Sets the current view to display
   *
   * @param {String} view - Current view to display - [main, loading, results-preview, results]
   */
  setView(view) {
    this.elements.views.forEach((el) => {
      if (el.dataset.view === view) {
        el.classList.remove("hidden");
        this.setState("view", view);
      } else {
        el.classList.add("hidden");
      }
    });
  }

  resetQuiz() {
    // Updates state
    const scoringDataClone = this.getDeepClone(window.matQuiz.scoringData);
    this.setState("scoringData", scoringDataClone);
    this.setState("topProducts", []);
    this.setState("userAnswers", []);
    this.setState("currentQuestionIndex", 1);
    this.setState("userAnswersData", []);
    this.setState("userCustomProperties", []);
    this.setState("productResultsData", []);

    // Updates views
    this.showCurrentQuestion(1);
    this.setView("main");

    // Update DOM elements
    this.elements.progressBarItem.forEach((el, index) => {
      el.classList.remove("is-current", "is-complete");
      if (index === 0) {
        el.classList.add("is-current");
      }
    });
    this.elements.navBtn.forEach((el, index) => {
      if (el.dataset.direction === "next") {
        el.setAttribute("disabled", "");
      }
    });
    this.elements.answerInput.forEach((el) => (el.checked = false));
    this.elements.resultsList.replaceChildren();
  }

  /**
   * Deep clone an object
   *
   * @param {Object} obj - Nested object to clone
   * @returns {Object} a deep-cloned object
   */
  getDeepClone(obj) {
    return JSON.parse(JSON.stringify(obj));
  }

  /**
   * Format Shopify styled prices as normal prices with currency
   *
   * @param {Number} number - The product price in Shopify format
   * @returns formatted price with currency
   */
  formatMoney(number) {
    const numberFormatted = number / 100;
    return new Intl.NumberFormat(window.Shopify.locale, {
      style: "currency",
      currency: window.Shopify.currency.active,
      minimumFractionDigits: 2,
    }).format(numberFormatted);
  }

  onNavBtnClick(event) {
    const el = event.currentTarget;
    const direction = el.dataset.direction;
    const currentQuestionIndex = this.getState("currentQuestionIndex");

    if (direction === "next") {
      // Submit form if the current question is last
      if (currentQuestionIndex === this.getState("lastQuestionIndex")) {
        this.elements.form.dispatchEvent(new Event("submit"));

        return;
      }
      this.showCurrentQuestion(currentQuestionIndex + 1);
      this.updateProgressBar(currentQuestionIndex + 1, true);
      this.markProgressBarDone(currentQuestionIndex);
    } else {
      this.showCurrentQuestion(currentQuestionIndex - 1);
      this.updateProgressBar(currentQuestionIndex - 1);
    }
  }

  onAnswerChange(event) {
    const el = event.currentTarget;
    const nextButton = el
      .closest("[data-js-question]")
      .querySelector('[data-direction="next"]');
    // allow the user to proceed to the next question
    nextButton.removeAttribute("disabled");
  }

  onFormSubmit(event) {
    event.preventDefault();
    const formData = new FormData(this.elements.form);
    const answers = [];

    for (var pair of formData.entries()) {
      const key = pair[0];
      const value = pair[1];

      // Do not include data from appended inputs in theme customizer
      if (key.includes("q")) {
        answers.push({
          [key]: value,
        });
      }
    }

    this.setView("loading");
    this.setState("userAnswers", answers);
    this.scoreProducts();
    this.getTopResults();
    this.fetchAllProductData();
    this.setUserAnswersData();
    this.renderUserAnswersList(this.getState("userAnswersData"));
  }

  /**
   * Shows the next/previous question and hides all others
   *
   * @param {Number} newIndex - The index of the question to update
   */
  showCurrentQuestion(newIndex) {
    const questionEl = this.querySelector(
      `[data-js-question][data-index="${newIndex}"]`
    );
    const currentQuestionEl = this.querySelector(".mq-card--active");

    // Update card UI
    currentQuestionEl.classList.remove("mq-card--active");
    if (questionEl) {
      questionEl.classList.add("mq-card--active");
    }

    // update state
    this.setState("currentQuestionIndex", newIndex);
  }

  /**
   * Updates the progress bar UI
   *
   * @param {Number} index - The index of the progress bar item to update
   */
  updateProgressBar(index) {
    const progressEl = this.querySelector(
      `[data-progress-bar-item][data-index="${index}"]`
    );
    const currentProgressEl = this.querySelector(
      `[data-progress-bar-item].is-current`
    );

    if (progressEl) {
      currentProgressEl.classList.remove("is-current");
      progressEl.classList.add("is-current");
    }
  }

  markProgressBarDone(index) {
    const progressEl = this.querySelector(
      `[data-progress-bar-item][data-index="${index}"]`
    );
    if (progressEl) {
      progressEl.classList.add("is-complete");
    }
  }

  scoreProducts() {
    const products = this.getState("scoringData");
    products.forEach((product) => {
      const questions = product.questions;
      const userAnswers = this.getState("userAnswers");
      let points = product.points;
      let tally = product.tally;

      userAnswers.forEach((answer) => {
        const key = Object.keys(answer)[0];
        const value = Object.values(answer)[0];
        const matchedQuestion = questions[key];
        const matchedAnswer = matchedQuestion.find(
          (answer) => Object.keys(answer)[0] === value
        );
        const answerPoints = Object.values(matchedAnswer)[0];
        points = points + answerPoints;

        if (answerPoints !== 0) {
          tally = tally + 1;
        }

        product.points = points;
        product.tally = tally;
      });
    });

    // Update the product scoring state
    this.setState("scoringData", products);
  }

  getTopResults() {
    const products = this.getState("scoringData");
    const sortedProducts = products.sort((a, b) => b.points - a.points);
    const topProducts = sortedProducts.slice(0, 3);
    this.setState("topProducts", topProducts);
  }

  setUserAnswersData() {
    const userAnswers = this.getState("userAnswers");
    const userResults = [];
    const userProperties = [];

    userAnswers.forEach((ua) => {
      const entries = Object.entries(ua);
      const [entry] = entries;
      const key = entry[0];
      const value = entry[1];
      const questionEl = this.querySelector(`[id="${key}"]`);
      const answerLabel = this.querySelector(`[id="${key}${value}"]`);
      userResults.push({
        question: questionEl.dataset.questionTitle,
        answer: answerLabel.dataset.answerTitle,
      });
      userProperties.push({
        label: questionEl.dataset.questionLabel,
        answer: answerLabel.dataset.answerTitle,
      });
    });

    this.setState("userAnswersData", userResults);
    this.setState("userCustomProperties", userProperties);
  }

  /**
   * Get data for a single product
   *
   * @param {String} handle - Product handle
   * @returns {Promise} - Promise object
   */
  fetchProductData(handle) {
    return fetch(`${window.Shopify.routes.root}products/${handle}.js`);
  }

  async fetchAllProductData() {
    const products = this.getState("topProducts");
    const handles = products.map((product) => product.handle);
    const data = [];

    if (products.length === 0) {
      return;
    }

    try {
      for (let handle = 0; handle < handles.length; handle++) {
        const h = handles[handle];
        const response = await this.fetchProductData(h);
        const json = await response.json();
        data.push(json);
      }
      this.renderResults(data);
      this.setView("results-preview");
      this.setState("productResultsData", data);
    } catch (error) {
      this.setView("error");
      console.error(`Failed to get all product data from Shopify: ${error}`);
    }
  }

  async fetchProductResultsSection() {
    const url = `${
      window.Shopify.routes.root + "?sections=mat-quiz-product-results"
    }`;
    return fetch(url);
  }

  /**
   * Append the product data onto the page
   *
   * @param {Array} products
   */
  renderResults(products) {
    products.forEach((product, index) => {
      const template = this.renderProductCard(product, index);
      const div = document.createElement("div");
      const container = this.elements.resultsList;
      div.innerHTML = template;
      div.classList.add("grid__item");
      container.appendChild(div);
    });
  }

  /**
   * Renders a single product card HTML
   *
   * @param {Object} product - Shopify product object
   * @param {Number} index - the product's position in the results array
   * @returns a single product card
   */
  renderProductCard(product, index) {
    const image = product.media[0] || null;
    const colorOption = product.options.find(
      (option) =>
        option.name.toLowerCase() ===
        window.theme.locale.products.variants.color_option.toLowerCase()
    );
    const colorOptionValues = colorOption ? colorOption.values : [];

    return `
      <div class="card-wrapper">
        <a href="${product.url}" class="full-unstyled-link card-link">
          <div class="center">
            <h3 class="h5">
            ${window.matQuiz.locale.results_page["match_" + (index + 1)]}
            </h3>
          </div>
          <div class="card card--product">
            <div class="card__inner">
              ${image !== null ? this.renderProductImage(image, product) : ""}
            </div>
          </div>
          <div class="card-information">
            <div class="card-information__wrapper">
              <div class="information__variant-wrapper d-flex justify-content-between">
                <div class="information__variant-main">
                  <span class="card-information__text h2">
                    ${product.title}
                  </span>
                  <span class="card-information__color">
                    ${this.renderColorCount(colorOptionValues.length)}
                  </span>
                </div>
                <div class="information__variant-price">
                  ${this.renderProductPrice(product)}
                </div>
              </div>
            </div>
          </div>
        </a>
      </div>
    `;
  }

  /**
   * Render the product card's feature image HTML
   *
   * @param {Object} image - Shopify image object
   * @param {Object} product - Shopify product object
   * @returns featured image HTML
   */
  renderProductImage(image, product) {
    const cardInnerAspectRatio = image
      ? `${(1 / image.aspect_ratio) * 100}%`
      : "100%";
    return `
      <div class="media media--transparent media--adapt media--hover-effect" style="padding-bottom: ${cardInnerAspectRatio};">
        <img src="${image.src}" alt="${product.title} - ${image.alt}" class="motion-reduce card-mainimg" width="${image.width}" height="${image.height}" />
      </div>      
    `;
  }

  /**
   * Render the product price HTML
   *
   * @param {Object} product - Shopify product object
   * @returns product price HTML
   */
  renderProductPrice(product) {
    const { compare_at_price, price, price_varies, price_min, price_max } =
      product;
    const priceVariesHTML = `<span> ${this.formatMoney(
      price_min
    )} - ${this.formatMoney(price_max)} </span>`;

    if (compare_at_price > price) {
      if (price_varies) {
        return priceVariesHTML;
      } else {
        return `<span> ${this.formatMoney(price)} <s>${this.formatMoney(
          compare_at_price
        )}</s></span>`;
      }
    } else {
      if (price_varies) {
        return priceVariesHTML;
      } else {
        return `<span>${this.formatMoney(price)}</span>`;
      }
    }
  }

  /**
   * Render the available color count of a product
   *
   * @param {Number} colorCount - The number of colors available of a product
   * @returns {String} - The color count HTML
   */
  renderColorCount(colorCount) {
    if (colorCount === 0) {
      return "";
    } else if (colorCount === 1) {
      return `${colorCount} ${window.matQuiz.locale.product.color_count}`;
    } else if (colorCount > 1) {
      return `${colorCount} ${window.matQuiz.locale.product.color_count_plural}`;
    }
  }

  /**
   * Displays a list of what the user answered
   *
   * @param {Array} results - Array of objects containing question and answer text
   * @returns answers results HTML
   */
  renderUserAnswersList(results) {
    const listItems = results
      .map((result) => {
        return this.renderUserAnswer(result);
      })
      .join("");

    const template = `
      <h2 class="h3">${window.matQuiz.locale.results_page.user_answered}</h2>
      <ol class="mq-user-answers-list">${listItems}</ol>
    `;
    const container = document.getElementById("results-users-answers");
    container.innerHTML = template;
  }
  /**
   * Render a single list item of question/answer
   *
   * @param {Object} result - Object with question/answer
   * @returns a single list item HTML
   */

  renderUserAnswer(result) {
    return `<li>${result.question} - <i>${result.answer}</i></li>`;
  }
}

customElements.define("mat-quiz", MatQuiz);
